import type { Filter, Query, ResultSet } from '@cubejs-client/core'

import { buildCubeQuery } from '@/lib/cube'
import { toNumber } from '@/lib/cube-report'

/**
 * İK view'ları (cube/model/passgage/ik.yml). Firma kısıtı Cube'da (`firm-scope.js`) uygulanır;
 * buradaki firma filtresi yalnız ekran seçimidir.
 */
export const HR_VIEWS = {
  personel: 'IkPersonel',
  hareket: 'IkHareket',
  izin: 'IkIzin',
  duzeltme: 'IkIzinDuzeltme',
} as const

export interface PersonelRow {
  personelKey: string
  firmaTablo: string | null
  diaKey: string | null
  sicilno: string
  adisoyadi: string | null
  firma: string | null
  sube: string | null
  pozisyon: string | null
  iseBaslama: string | null
  diaDurum: string | null
  passgageDurum: string
  passgageAktif: boolean | null
  passgageId: string | null
  sonHata: string | null
  sonDeneme: string | null
}

export interface HareketRow {
  id: string
  tip: string | null
  zaman: string | null
  sicilno: string | null
  adisoyadi: string | null
  firma: string | null
  sube: string | null
  durum: string
  hataKategori: string | null
  hataMetni: string | null
  denemeSayisi: number | null
  sonDeneme: string | null
  diaKey: string | null
  karar: string | null
  kararAciklama: string | null
  kararKullanici: string | null
  kapsayanIzinId: string | null
  /** `passgage` ya da `panel` (panelden onaylı talep ile DİA'ya eklenmiş, Passgage'de yok). */
  kaynak: string | null
}

export interface IzinRow {
  id: string
  izinTuru: string | null
  sicilno: string | null
  adisoyadi: string | null
  firma: string | null
  sube: string | null
  aciklama: string | null
  baslangic: string | null
  bitis: string | null
  saatlik: boolean | null
  sureDakika: number | null
  passgageDurum: string | null
  durum: string
  hataKategori: string | null
  sonHata: string | null
  denemeSayisi: number | null
  sonDeneme: string | null
  diaIzinKey: string | null
  karar: string | null
  kararAciklama: string | null
  kararKullanici: string | null
}

export interface DuzeltmeRow {
  id: string
  leaveId: string | null
  sicilno: string | null
  adisoyadi: string | null
  firma: string | null
  izinTuru: string | null
  hareketId: string | null
  hareketTuru: string | null
  hareketZamani: string | null
  eskiBas: string | null
  eskiBit: string | null
  yeniBas: string | null
  yeniBit: string | null
  sonuc: string | null
  hata: string | null
  olusturma: string | null
}

export const PERSONEL_MEMBERS = [
  'personelKey',
  'firmaTablo',
  'diaKey',
  'sicilno',
  'adisoyadi',
  'firma',
  'sube',
  'pozisyon',
  'iseBaslama',
  'diaDurum',
  'passgageDurum',
  'passgageAktif',
  'passgageId',
  'sonHata',
  'sonDeneme',
] as const satisfies readonly (keyof PersonelRow)[]

export const HAREKET_MEMBERS = [
  'id',
  'tip',
  'zaman',
  'sicilno',
  'adisoyadi',
  'firma',
  'sube',
  'durum',
  'hataKategori',
  'hataMetni',
  'denemeSayisi',
  'sonDeneme',
  'diaKey',
  'karar',
  'kararAciklama',
  'kararKullanici',
  'kapsayanIzinId',
  'kaynak',
] as const satisfies readonly (keyof HareketRow)[]

export const IZIN_MEMBERS = [
  'id',
  'izinTuru',
  'sicilno',
  'adisoyadi',
  'firma',
  'sube',
  'aciklama',
  'baslangic',
  'bitis',
  'saatlik',
  'sureDakika',
  'passgageDurum',
  'durum',
  'hataKategori',
  'sonHata',
  'denemeSayisi',
  'sonDeneme',
  'diaIzinKey',
  'karar',
  'kararAciklama',
  'kararKullanici',
] as const satisfies readonly (keyof IzinRow)[]

export const DUZELTME_MEMBERS = [
  'id',
  'leaveId',
  'sicilno',
  'adisoyadi',
  'firma',
  'izinTuru',
  'hareketId',
  'hareketTuru',
  'hareketZamani',
  'eskiBas',
  'eskiBit',
  'yeniBas',
  'yeniBit',
  'sonuc',
  'hata',
  'olusturma',
] as const satisfies readonly (keyof DuzeltmeRow)[]

const NUMERIC = new Set<string>(['denemeSayisi', 'sureDakika'])
const BOOLEAN = new Set<string>(['passgageAktif', 'saatlik'])

/** Ekran filtreleri -> Cube filtreleri. Boş liste filtre üretmez. */
export interface HrFilterInput {
  view: string
  equals?: Record<string, string[]>
  /** Ad ya da sicilde geçen metin. */
  search?: string
  /** `member` için tarih aralığı (takvim günü, İstanbul). */
  dateMember?: string
  from?: string
  to?: string
}

export function hrFilters({ view, equals = {}, search, dateMember, from, to }: HrFilterInput) {
  const filters: Filter[] = []
  for (const [member, values] of Object.entries(equals)) {
    if (values.length > 0) filters.push({ member: `${view}.${member}`, operator: 'equals', values })
  }
  const term = search?.trim()
  if (term) {
    filters.push({
      or: [
        { member: `${view}.adisoyadi`, operator: 'contains', values: [term] },
        { member: `${view}.sicilno`, operator: 'contains', values: [term] },
      ],
    })
  }
  if (dateMember && from) {
    filters.push({ member: `${view}.${dateMember}`, operator: 'afterOrOnDate', values: [from] })
  }
  if (dateMember && to) {
    filters.push({ member: `${view}.${dateMember}`, operator: 'beforeOrOnDate', values: [to] })
  }
  return filters
}

export interface HrPageInput {
  view: string
  members: readonly string[]
  filters: Filter[]
  /** Sütun -> azalan mı; satır anahtarı sona eklenir (sayfalar arası kararlı sıra). */
  order: { id: string; desc: boolean }[]
  rowKey: string
  limit: number
  offset: number
}

export function hrRowsQuery({ view, members, filters, order, rowKey, limit, offset }: HrPageInput) {
  const sort: [string, 'asc' | 'desc'][] = order.map((item) => [
    `${view}.${item.id}`,
    item.desc ? 'desc' : 'asc',
  ])
  if (!order.some((item) => item.id === rowKey)) sort.push([`${view}.${rowKey}`, 'asc'])
  return buildCubeQuery({
    dimensions: members.map((member) => `${view}.${member}`),
    filters,
    order: sort,
    limit,
    offset,
    ungrouped: true,
  })
}

/** Filtrelenmiş satır sayısı ve view'ın ek ölçüleri (ör. aktif/pasif). */
export function hrMeasuresQuery(view: string, measures: string[], filters: Filter[]): Query {
  return buildCubeQuery({ measures: measures.map((m) => `${view}.${m}`), filters })
}

/** Bir boyutun değerleri ve satır sayısı (filtre seçenekleri, şube kırılımı). */
export function hrBreakdownQuery(
  view: string,
  dimensions: string[],
  measures: string[],
  filters: Filter[] = [],
): Query {
  return buildCubeQuery({
    dimensions: dimensions.map((d) => `${view}.${d}`),
    measures: measures.map((m) => `${view}.${m}`),
    filters,
    order: dimensions.map((d) => [`${view}.${d}`, 'asc'] as [string, 'asc']),
    limit: 1000,
  })
}

function cell(member: string, value: unknown): unknown {
  if (value === undefined || value === null) return null
  if (NUMERIC.has(member)) return toNumber(value)
  if (BOOLEAN.has(member)) return value === true || value === 'true' || value === 't'
  return value
}

export function mapHrRows<T>(view: string, members: readonly string[]) {
  return (result: ResultSet): T[] =>
    (result.rawData() as Record<string, unknown>[]).map(
      (row) =>
        Object.fromEntries(
          members.map((member) => [member, cell(member, row[`${view}.${member}`])]),
        ) as T,
    )
}

/** Ölçü satırı: `{ count: 12, aktifSayisi: 10 }`. */
export function mapMeasures(view: string, measures: string[]) {
  return (result: ResultSet): Record<string, number> => {
    const row = (result.rawData() as Record<string, unknown>[])[0] ?? {}
    return Object.fromEntries(measures.map((m) => [m, toNumber(row[`${view}.${m}`]) ?? 0]))
  }
}

/** Kırılım satırları: boyutlar metin, ölçüler sayı. */
export function mapBreakdown(view: string, dimensions: string[], measures: string[]) {
  return (result: ResultSet) =>
    (result.rawData() as Record<string, unknown>[]).map((row) => ({
      ...Object.fromEntries(
        dimensions.map((d) => [d, (row[`${view}.${d}`] as string | null) ?? null]),
      ),
      ...Object.fromEntries(measures.map((m) => [m, toNumber(row[`${view}.${m}`]) ?? 0])),
    })) as (Record<string, string | null> & Record<string, number>)[]
}
